/**
 * The brief flow: compose, request, price, approve.
 *
 * Spec 2026-09-20 (work-cards, Alan): the submit is a REQUEST, not a chat send.
 * One "Quote this" button inside the composer. Tapping it locks the
 * brief (read-only, dimmed), shows "Reading your brief…", then the price with
 * three honest ready-to-go lines and "Hold to approve €X" - the same
 * SwipeToConfirm gesture as a Work card, in place, no sheet (Alan 2026-09-26).
 * Approving lands the customer in Work, where the card is already "finding your
 * editor". "Recent" is gone from Start: every running briefing lives in Work now.
 *
 * `#start/<submissionId>` opens this screen with that submission's price block
 * - it is where a "To confirm" card in Work points.
 *
 * MONEY PATH. Rules, each with a reason:
 *
 * 1. THE PRICE SHOWN IS the child briefs' `fareCents` — the column
 *    activation-service charges. The submission total is their sum. Never a
 *    second source for a price.
 * 2. `/api/briefs/assess` IS NEVER CALLED from here. It re-runs the assessor and
 *    can quote a different number for identical text.
 * 3. NO CLIENT IDEMPOTENCY TOKEN, deliberately. The bundle confirm is idempotent
 *    per child server-side.
 * 4. A submission with an unpriced child NEVER shows an approve button or a
 *    €0,00. It says so, and offers "Price again" (spec pricing-never-stops).
 *
 * @module web/screens/app/BriefScreen
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ApiError, get, post } from "../../api/client.ts";
import type { BriefSubmissionEnvelope, BriefSubmissionResponse, NormalizedBrief } from "../../api/types.ts";
import { fmtEur, fmtEurCompact } from "../../money.ts";
import { useBrand } from "../../state/BrandContext.tsx";
import { announceBriefsChanged, confirmResult, postConfirm } from "./confirmSheet.tsx";
import { SwipeToConfirm, type SwipeResult } from "./work/SwipeToConfirm.tsx";
import { paySource } from "../../paySource.ts";
import { chipsFor, readingLine } from "./work/readiness.ts";
import { repriceMessage, type RepriceResult } from "./work/cards.ts";
import { ContentFolderOffer, showContentOffer } from "./ContentFolderOffer.tsx";

import { PricingRobot } from "./PricingRobot.tsx";
import { useFlatRate } from "../../state/useFlatRate.ts";
import { isFlatRateBrief } from "./work/cards.ts";
import { isConfirmationInProgress } from "./confirmation.ts";
import { forgetDemoBrief, readDemoBrief } from "../onboarding/Shell.tsx";

const LINK_RE = /https?:\/\/\S+/i;

/** Parse a JSON column, tolerating null and malformed content. */
function parseJson<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

/** € from cents, two decimals, IE grouping. */
function euros(cents: number): string {
  return (cents / 100).toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

type Phase = "compose" | "pricing" | "priced";

/**
 * One box from tap to price (Alan 2026-09-26: "smoother, motion-graphic").
 * The Request button, the calculating bar and the quote take turns in one
 * slot; this glides the slot's height between them instead of cutting. Colour,
 * Robo and the content reveal are CSS (brief.css, "One box"). Forward only -
 * Edit and errors snap back, and reduced motion never moves the box.
 */
function useSlotMorph(phase: Phase): (el: HTMLElement | null) => void {
  const slot = useRef<HTMLElement | null>(null);
  const last = useRef<{ phase: Phase; h: number } | null>(null);
  const anim = useRef<Animation | null>(null);
  const leaving = useRef(0);
  useLayoutEffect(() => {
    const el = slot.current;
    const prev = last.current;
    const left = leaving.current;
    leaving.current = 0;
    if (!el) { last.current = null; return; }
    // A quote that lands mid-morph grows on from where the box is, not from its target.
    const from = anim.current?.playState === "running" && (anim.current.effect as KeyframeEffect | null)?.target === el
      ? el.getBoundingClientRect().height
      : left || prev?.h || 0;
    anim.current?.cancel();
    const to = el.offsetHeight;
    last.current = { phase, h: to };
    const forward = (prev?.phase === "compose" && phase === "pricing") || (prev?.phase === "pricing" && phase === "priced");
    if (!forward || !from || Math.abs(from - to) < 1 || typeof el.animate !== "function") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    el.style.overflow = "hidden";
    const a = el.animate([{ height: `${from}px` }, { height: `${to}px` }], {
      duration: phase === "pricing" ? 320 : 520,
      easing: "cubic-bezier(0.32, 0.72, 0, 1)",
    });
    a.onfinish = a.oncancel = () => { el.style.overflow = ""; };
    anim.current = a;
  }, [phase]);
  return useCallback((el: HTMLElement | null) => {
    // Measured on the way out, while still in the page: the button's height at
    // the tap. A height taken at mount can predate the settled layout.
    if (!el && slot.current) leaving.current = slot.current.offsetHeight;
    slot.current = el;
  }, []);
}

export function BriefScreen({ submissionId = null, onAddContent, contentRevision = 0 }: { submissionId?: string | null; onAddContent: () => void; contentRevision?: number }) {
  const { brand, refresh, notify, setDraftDirty } = useBrand();
  const { plan, loading: planLoading, failed: planFailed, reload: reloadPlan } = useFlatRate();
  const planBlocked = planLoading || planFailed || !!(plan && (!plan.active || plan.remaining <= 0));
  const draftKey = brand ? `aditor-brief-draft:${brand.self?.id ?? "brand-link"}:${brand.id}` : null;
  const [raw_, setRaw] = useState(() => draftKey ? window.sessionStorage.getItem(draftKey) || readDemoBrief() : "");
  // The script priced on /demo or /pricing fills the first brief once; the session draft keeps it from here.
  useEffect(() => { if (draftKey) forgetDemoBrief(); }, [draftKey]);
  const [phase, setPhase] = useState<Phase>("compose");
  const [submission, setSubmission] = useState<BriefSubmissionResponse | null>(null);
  const [repricing, setRepricing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const approvalEpoch = useRef(0);
  /** The quote "Edit brief" was tapped on; the next request edits it in place (same briefing, new version). */
  const editingFrom = useRef<string | null>(null);
  const completionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pdfInput = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const loadedId = useRef<string | null>(null);
  const slot = useSlotMorph(phase);

  useEffect(() => {
    setConfirming(false);
    return () => {
      approvalEpoch.current += 1;
      if (completionTimer.current) clearTimeout(completionTimer.current);
    };
  }, [draftKey, submissionId, submission?.id]);

  // The durable worker may finish after the POST. Keep reading its state,
  // including when a user reopens an already-processing paid briefing.
  useEffect(() => {
    if (!submission || !isConfirmationInProgress(submission.status)) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const id = submission.id;
    async function observe() {
      try {
        const next = await get<BriefSubmissionEnvelope>(`/api/brief-submissions/${id}`);
        if (!active) return;
        setSubmission(next.submission);
        if (!isConfirmationInProgress(next.submission.status)) {
          announceBriefsChanged();
          void refresh();
          return;
        }
      } catch { /* A read failure does not authorize another payment request. */ }
      if (active) timer = setTimeout(observe, 1500);
    }
    timer = setTimeout(observe, 1500);
    return () => { active = false; clearTimeout(timer); };
  }, [submission?.id, submission?.status, refresh]);

  useEffect(() => {
    if (draftKey) {
      if (phase === "compose" && raw_.trim()) window.sessionStorage.setItem(draftKey, raw_);
      else window.sessionStorage.removeItem(draftKey);
    }
    setDraftDirty(phase === "compose" && !!raw_.trim());
    return () => setDraftDirty(false);
  }, [draftKey, raw_, phase, setDraftDirty]);

  useEffect(() => {
    const t = box.current;
    if (!t) return;
    if (!raw_) { t.style.height = ""; return; }
    t.style.height = "auto";
    t.style.height = `${Math.min(t.scrollHeight, 420)}px`;
  }, [raw_]);

  // A "To confirm" card in Work points here with its submission id.
  useEffect(() => {
    if (!submissionId || loadedId.current === submissionId) return;
    loadedId.current = submissionId;
    let live = true;
    setPhase("pricing");
    void get<BriefSubmissionEnvelope>(`/api/brief-submissions/${submissionId}`)
      .then(r => {
        if (!live) return;
        setSubmission(r.submission);
        setRaw(r.submission.briefs.map(b => b.raw).filter(Boolean).join("\n\n"));
        setPhase("priced");
      })
      .catch(() => {
        if (!live) return;
        notify("That briefing isn't here anymore.", "error");
        setPhase("compose");
        window.location.hash = "start";
      });
    return () => { live = false; };
  }, [submissionId, notify]);

  const locked = phase !== "compose";
  const ready = LINK_RE.test(raw_) || raw_.trim().length >= 12;

  /** Apply a submission envelope from either intake route (typed text or PDF). */
  function applyEnvelope(res: BriefSubmissionEnvelope): void {
    if (plan && ["confirmation_requested", "processing", "confirmed", "confirmation_failed"].includes(res.submission.status)) {
      setRaw("");
      setSubmission(null);
      setPhase("compose");
      announceBriefsChanged();
      void refresh();
      window.location.hash = "work";
      return;
    }
    setSubmission(res.submission);
    setPhase("priced");
    if (res.replaced) notify("This replaces your earlier version - only this one counts.", "success");
    // The new submission is a "To confirm" card in Work now; the badge follows.
    announceBriefsChanged();
  }

  async function request(): Promise<void> {
    const text = raw_.trim();
    if (text.length < 10 || planBlocked) return;
    setPhase("pricing");
    setSubmission(null);
    try {
      const editing = editingFrom.current;
      if (editing) {
        applyEnvelope(await post<BriefSubmissionEnvelope>(`/api/brief-submissions/${encodeURIComponent(editing)}/edit`, { raw: text }));
        notify("Updated - same briefing, new price.", "success");
      } else {
        applyEnvelope(await post<BriefSubmissionEnvelope>("/api/briefs", { raw: text }));
      }
      editingFrom.current = null;
    } catch (e) {
      if (editingFrom.current && e instanceof ApiError && (e.status === 409 || e.status === 422)) {
        // Confirmed meanwhile, or the new text couldn't be read: the current quote stands, the text stays.
        notify(e.message, "error");
        setPhase("compose");
        return;
      }
      notify(plan ? (e instanceof ApiError ? e.message : "Could not send this brief. Check Work before trying again.") : "Could not price this brief. Try again.", "error");
      if (plan) reloadPlan();
      setPhase("compose");
    }
  }

  /** Read a File as a base64 data: URL for the PDF intake route. */
  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error ?? new Error("Could not read the file"));
      reader.readAsDataURL(file);
    });
  }

  async function requestPdf(file: File): Promise<void> {
    if (planBlocked) return;
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) {
      notify("That doesn't look like a PDF. Upload a .pdf file, or paste the briefing text.", "error");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      notify("This PDF is too large. Split it up, or paste the briefing text.", "error");
      return;
    }
    setPhase("pricing");
    setSubmission(null);
    try {
      const pdfBase64 = await fileToBase64(file);
      applyEnvelope(await post<BriefSubmissionEnvelope>("/api/briefs/pdf", { pdfBase64 }));
    } catch (e) {
      notify(e instanceof ApiError ? e.message : "Could not read this PDF. Paste the briefing text, or try again.", "error");
      if (plan) reloadPlan();
      setPhase("compose");
    }
  }

  /** Unlock the field. The text stays - a customer's words are never thrown away. */
  function edit(): void {
    editingFrom.current = submission?.id ?? editingFrom.current;
    approvalEpoch.current += 1;
    if (completionTimer.current) clearTimeout(completionTimer.current);
    setSubmission(null);
    setPhase("compose");
    if (window.location.hash.startsWith("#start/")) window.location.hash = "start";
    requestAnimationFrame(() => box.current?.focus());
  }

  /**
   * The hold's tap. Same request and sentences as a Work card (confirmSheet.tsx).
   * On success the check gets its moment in the box, then the customer lands in
   * Work (peak-end), where the card already says "finding your editor".
   */
  async function approve(sub: BriefSubmissionResponse): Promise<SwipeResult> {
    const operation = ++approvalEpoch.current;
    setConfirming(true);
    let res = await postConfirm(`/api/brief-submissions/${sub.id}/confirm`, sub.amountDueCents);
    // A lost response can hide a committed request. Reconcile before offering
    // another gesture; server idempotency remains the final authority.
    if (res.status === 0 || res.status >= 500) {
      try {
        const observed = await get<BriefSubmissionEnvelope>(`/api/brief-submissions/${sub.id}`);
        if (observed.submission.status === "confirmed" || isConfirmationInProgress(observed.submission.status)) {
          res = { status: observed.submission.status === "confirmed" ? 200 : 202, body: observed };
        }
      } catch { /* Keep the original error, never invent success. */ }
    }
    if (operation !== approvalEpoch.current) return { ok: false, message: "The active briefing changed. Check Work for its status." };
    if (res.body?.submission && isConfirmationInProgress(res.body.submission.status)) {
      setSubmission(res.body.submission);
      setConfirming(false);
      return { ok: false, message: "Confirming automatically. You do not need to tap again." };
    }
    if (res.status === 409 && res.body?.code === "quote_changed" && res.body.submission) {
      setSubmission(res.body.submission);
      setConfirming(false);
      return { ok: false, message: "The price changed. Review the new quote before approving." };
    }
    const result = confirmResult(res, sub.briefs.length > 1);
    if (result.ok) {
      announceBriefsChanged();
      completionTimer.current = setTimeout(() => {
        if (operation !== approvalEpoch.current) return;
        setRaw("");
        setSubmission(null);
        setPhase("compose");
        void refresh();
        window.location.hash = "work";
      }, 1400);
    } else setConfirming(false);
    return result;
  }

  async function priceAgain(sub: BriefSubmissionResponse, noVoiceOver = false): Promise<void> {
    setRepricing(true);
    try {
      const res = await post<BriefSubmissionEnvelope & RepriceResult>(`/api/brief-submissions/${sub.id}/reprice`, noVoiceOver ? { noVoiceOver: true } : undefined);
      setSubmission(res.submission);
      announceBriefsChanged();
      const m = repriceMessage(res);
      notify(m.text, m.tone);
    } catch {
      notify("Couldn't price it this time - try again in a moment.", "error");
    } finally {
      setRepricing(false);
    }
  }

  return (
    <>
      <div className="brief-flow" data-phase={phase}>
        <h1 className="brief-h">What are we making?</h1>
        <p className="brief-intro">Drop in a script, an idea, or a public Google Doc.</p>

        {plan && <div className="card" style={{ marginBottom: 24 }}>
          <p className="t-body-sm">{plan.active ? `${plan.remaining} of ${plan.cardLimit} cards remaining this cycle · Included in your plan` : "Your board is open. Activate your plan before sending new briefs."}</p>
          {!plan.active && (brand?.role === "owner" ? <a className="btn btn-brand" href="#billing" style={{ marginTop: 16 }}>Activate plan</a> : <p className="t-body-sm" style={{ marginTop: 8 }}>Your brand owner can activate the plan in Billing.</p>)}
          {plan.active && plan.remaining === 0 && <p className="t-body-sm" style={{ marginTop: 8 }}>All cards for this cycle are used. New cards become available when your next cycle is paid.</p>}
        </div>}
        {planFailed && <div className="card" role="alert" style={{ marginBottom: 24 }}><p className="t-body-sm">We couldn't check your plan. Please retry before sending a brief.</p><button className="btn btn-ghost" style={{ marginTop: 16 }} onClick={reloadPlan}>Retry plan check</button></div>}

        <div className={`brief-input-well${locked ? " locked" : ""}`}>
          <label className="brief-label" htmlFor="brief-raw">Your brief</label>
          <textarea
            id="brief-raw"
            ref={box}
            rows={3}
            value={raw_}
            readOnly={locked}
            aria-readonly={locked}
            onChange={e => setRaw(e.target.value)}
            onKeyDown={e => { if (!locked && (e.metaKey || e.ctrlKey) && e.key === "Enter" && ready) void request(); }}
            placeholder="Paste your script or describe your ad…"
          />
          {!locked && <div className="brief-toolbar">
            <input
              ref={pdfInput}
              type="file"
              accept="application/pdf,.pdf"
              hidden
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void requestPdf(f); }}
            />
            <button
              type="button"
              className="btn btn-ghost brief-pdf"
              disabled={planBlocked}
              onClick={() => pdfInput.current?.click()}
              title="Upload a PDF briefing"
            >
              Upload PDF
            </button>
            <button
              type="button"
              className="btn btn-brand brief-request"
              id="brief-request-btn"
              ref={slot}
              disabled={!ready || planBlocked}
              onClick={() => void request()}
              title="Quote this (⌘↵)"
            >
              {planLoading ? "Checking your plan…" : "Quote this"}
            </button>
          </div>}
          {locked && (
            <span className="brief-lock" aria-hidden="true" title="Locked while we read your brief">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
            </span>
          )}
        </div>

        {(!locked || phase === "pricing") && (
            <div className="brief-inhint">
              {LINK_RE.test(raw_) ? <><span className="lk">↳</span> We'll read your Google Doc. One public link, or just the text.</> : ""}
            </div>
        )}

        {!locked && <p className="brief-assure">
          {plan ? "Each creative uses one card · Sent directly to Work" : !planLoading && !planFailed ? "You'll see the price before anything is charged · 48h to your editor" : ""}
        </p>}

        {locked && (
          <section ref={slot} className={`brief-ready${phase === "pricing" ? " is-calculating" : ""}`} id="brief-ready" aria-live="polite">
            {phase === "pricing" && (
              <PricingRobot />
            )}

            {phase === "priced" && submission && (
              <ReadyBlock
                submission={submission}
                brand={brand}
                repricing={repricing}
                confirming={confirming}
                onApprove={() => approve(submission)}
                onPriceAgain={() => void priceAgain(submission)}
                onNoVoiceOver={() => void priceAgain(submission, true)}
                onEdit={edit}
                onQuoteChange={setSubmission}
                onAddContent={onAddContent}
                contentRevision={contentRevision}
              />
            )}
          </section>
        )}
      </div>
    </>
  );
}

// ============================================================
// THE READY-TO-GO BLOCK
// ============================================================

function ReadyBlock({ submission, brand, repricing, confirming, onApprove, onPriceAgain, onNoVoiceOver, onEdit, onQuoteChange, onAddContent, contentRevision }: {
  submission: BriefSubmissionResponse;
  brand: ReturnType<typeof useBrand>["brand"];
  repricing: boolean;
  confirming: boolean;
  onApprove: () => Promise<SwipeResult>;
  onPriceAgain: () => void;
  onNoVoiceOver: () => void;
  onEdit: () => void;
  onQuoteChange: (next: BriefSubmissionResponse) => void;
  onAddContent: () => void;
  contentRevision: number;
}) {
  const s = submission.status;
  if (submission.briefs.length > 0 && submission.briefs.every(isFlatRateBrief)) {
    return <div className="brief-manual" style={{ display: "flex" }}><h2>Included in your plan</h2><p>{submission.message || "Follow your briefings in Work."}</p><a className="btn btn-brand" href="#work">Open Work</a></div>;
  }

  if (s === "confirmation_requested" || s === "processing") {
    return (
      <div className="brief-manual" style={{ display: "flex" }}>
        <h2>Confirming your briefings</h2>
        <p>Your payment and cards are being handled automatically. You do not need to tap again.</p>
        <a className="btn btn-ghost" href="#work">Open Work</a>
      </div>
    );
  }

  if (s === "confirmed") return <div className="brief-manual"><h2>Confirmed</h2><p>Your briefings are on their way to production.</p><a className="btn btn-brand" href="#work">Open Work</a></div>;

  if (s !== "ready" && s !== "confirmation_failed") {
    return (
      <div className="brief-manual" style={{ display: "flex" }}>
        <h2>{s === "needs_access" ? "We couldn't open that doc" : "Let's take a closer look"}</h2>
        <p>{submission.message}</p>
        <button type="button" className="btn btn-ghost brief-edit" onClick={onEdit}>Edit brief</button>
      </div>
    );
  }

  const unpriced = submission.briefs.some(b => b.fareCents === null);
  const norms = submission.briefs.map(b => parseJson<NormalizedBrief>(b.normalizedJson));
  const chips = chipsFor(norms[0] ?? null);
  const reading = readingLine(norms);
  const pay = paySource(brand, submission.amountDueCents);

  // No spoken words, no price (Alan 2026-10-03): an assumed 30 s followed by a
  // long script was the Rugsak loophole. The script, or an explicit "no voice-over".
  const unpricedBriefs = submission.briefs.filter(b => b.fareCents === null);
  if (unpricedBriefs.length > 0 && unpricedBriefs.every(b => b.needsReviewReason === "no_script")) {
    return (
      <div className="brief-manual brief-unpriced" id="brief-no-script" style={{ display: "flex" }}>
        <h2>We couldn't find the spoken script</h2>
        <p>The price depends on how long the voice-over is. Paste the script into your brief and we price it exactly. Nothing is charged.</p>
        <div className="brief-ready-actions">
          <button type="button" className="btn btn-brand brief-edit" id="brief-add-script-btn" onClick={onEdit}>Add the script</button>
          <button type="button" className="btn btn-ghost" id="brief-no-vo-btn" disabled={repricing} onClick={onNoVoiceOver}>
            {repricing ? "Pricing…" : "This ad has no voice-over"}
          </button>
        </div>
      </div>
    );
  }

  // Rule 4: never a €0,00 next to a button. Honest state plus the way out.
  if (unpriced) {
    return (
      <div className="brief-manual brief-unpriced" id="brief-unpriced" style={{ display: "flex" }}>
        <h2>We couldn't price this automatically yet</h2>
        <p>Nothing is charged. Tap <b>Price again</b> and the engine takes another run - your brief stays exactly as it is.</p>
        {submission.briefs.length > 1 && (
          <div className="brief-split-list">
            {submission.briefs.map(b => (
              <div className="brief-split-item" data-brief-id={b.id} key={b.id}>
                <div className="brief-split-main"><span className="brief-split-t">{b.title || "Briefing"}</span></div>
                <span className="brief-split-price">{b.fareCents === null ? "Not priced yet" : `€${euros(b.fareCents)}`}</span>
              </div>
            ))}
          </div>
        )}
        <div className="brief-ready-actions">
          <button type="button" className="btn btn-brand brief-again" id="brief-price-again-btn" disabled={repricing} onClick={onPriceAgain}>
            {repricing ? "Pricing…" : "Price again"}
          </button>
          <button type="button" className="btn btn-ghost brief-edit" onClick={onEdit}>Edit brief</button>
        </div>
      </div>
    );
  }

  return (
    <div className="brief-estimate-body">
      <div className="brief-price-label">
        {submission.briefs.length > 1 ? `We found ${submission.briefs.length} briefings` : "Your price"}
      </div>

      {submission.briefs.length > 1 ? (
        <>
          <div className="brief-split-list">
            {submission.briefs.map((b, i) => (
              <div className="brief-split-item" data-brief-id={b.id} key={b.id}>
                <div className="brief-split-main">
                  <span className="brief-split-t">{b.title || "Briefing"}</span>
                  <span className="brief-split-chips">{chipsFor(norms[i] ?? null).map(c => <span className={`schip${c.startsWith("Aditor Hooks") ? " schip-hooks" : ""}`} key={c}>{c}</span>)}</span>
                </div>
                <span className="brief-split-price">€{euros(b.fareCents ?? 0)}</span>
              </div>
            ))}
          </div>
          {submission.sharedInstructionsPresent && (
            <p className="brief-content-line">Shared instructions (like a common end card) are included on every briefing.</p>
          )}
          <div className="brief-split-total">
            <span>Total</span>
            <span className="brief-price" data-fare-cents={submission.totalFareCents}>
              <span className="cur">€</span>{euros(submission.totalFareCents)}
            </span>
          </div>
        </>
      ) : (
        <>
          <div className="brief-price reveal" data-fare-cents={submission.totalFareCents}>
            <span className="cur">€</span>{euros(submission.totalFareCents)}
          </div>
          <div className="brief-summary">
            {chips.map(c => <span className={`schip${c.startsWith("Aditor Hooks") ? " schip-hooks" : ""}`} key={c}>{c}</span>)}
          </div>
        </>
      )}

      {reading && <p className="brief-scope-note" id="brief-reading">{reading}</p>}
      {s === "confirmation_failed" && <p className="brief-scope-note" role="alert">{submission.message || "We could not finish confirmation. You can try again."}</p>}
      {norms.some(n => n?.flags?.includes("unreachable_link")) && (
        <details className="brief-link-note">
          <summary>A linked reference couldn't be read</summary>
          <p>This quote uses the material we could read. If a missing link contains spoken script, paste those words into your brief before approving.</p>
          <ul>{[...new Set(norms.flatMap(n => n?.unreachableLinks ?? []))]
            .filter(url => /^https?:\/\//i.test(url))
            .map(url => <li key={url}><a href={url} target="_blank" rel="noopener noreferrer">{url}</a></li>)}</ul>
        </details>
      )}
      {brand?.billingBlocked && <p className="brief-scope-note">Billing on hold — add a card to send this.</p>}

      {/* Only the saving is said, never the surcharge (Alan 2026-09-25: "NUR das
          sparen erwähnen, nicht dass die draufzahlen"). The fee stays inside the
          total; no line names it or its amount. */}
      {brand && showContentOffer(brand, submission) && (
        <ContentFolderOffer brand={brand} submission={submission} onQuoteChange={onQuoteChange} onAddContent={onAddContent} contentRevision={contentRevision} />
      )}

      <div className="brief-ready-actions">
        <SwipeToConfirm
          id="brief-confirm-btn"
          action={submission.status === "confirmation_failed" ? "Try again" : "Approve"}
          amount={pay.kind === "sponsored" ? "Sponsored" : fmtEurCompact(submission.amountDueCents)}
          disabled={submission.totalFareCents <= 0 || !!pay.blocked}
          onConfirm={onApprove}
        />
        {/* What the hold charges and how - the disclosure the sheet used to carry. */}
        <p className="brief-pay-note">{pay.kind === "no-card" ? pay.note : `${pay.label} · ${pay.note}`}</p>
        <button type="button" className="btn btn-ghost brief-edit" id="brief-edit-btn" disabled={confirming} onClick={onEdit}>Edit brief</button>
      </div>
    </div>
  );
}

export { fmtEur };
